import { t } from '@lingui/macro';
import { ExperienceCreatedFrom } from '@prismo-io/schemas';
import { z } from 'zod';

export const ExperienceTypeSchema = z.object(
  {
    id: z.string().trim().uuid(),
    name: z.string().trim().min(1),
  },
  {
    required_error: t`Le type de contrat est obligatoire.`,
    invalid_type_error: t`Le type de contrat n'est pas valide.`,
  }
);

export const DesignationSchema = z.object(
  {
    code: z.string().trim().min(1),
    referential_id: z.string().uuid(),
    name: z.string().trim().min(1),
  },
  {
    required_error: t`Le nom est obligatoire.`,
    invalid_type_error: t`Le nom est obligatoire.`,
  }
);

export const DomainSchema = z.object(
  {
    code: z.string().trim().min(1),
    referential_id: z.string().trim().uuid(),
    name: z.string().trim().min(1),
  },
  { required_error: t`Vous devez sélectionner au moins 1 domaine.` }
);

const ExperienceNameSchema = z.union(
  [
    DesignationSchema,
    z
      .string({
        required_error: t`Le nom est obligatoire.`,
        invalid_type_error: t`Le nom est obligatoire.`,
      })
      .trim(),
    // .min(1, t`Le nom est obligatoire.`),
  ],
  {
    required_error: t`Le nom est obligatoire.`,
    invalid_type_error: t`Le nom est obligatoire.`,
  }
);

export const ExperienceBaseSchema = z.object({
  id: z.string().uuid().nullish(),
  name: ExperienceNameSchema,
  domains: DomainSchema.array().nullish(),
  favorite: z.number({ coerce: true }).nullish(),
  contractType: ExperienceTypeSchema.nullable(),
  company: z
    .string({
      invalid_type_error: t`Le nom de l'entreprise n'est pas valide.`,
    })
    .trim()
    .nullish(),
  yearStart: z
    .date({
      coerce: true,
      invalid_type_error: t`La date n'est pas valide.`,
      required_error: t`La date n'est pas valide.`,
    })
    .nullish(),
  yearEnd: z
    .date({
      coerce: true,
      invalid_type_error: t`La date n'est pas valide.`,
      required_error: t`La date n'est pas valide.`,
    })
    .nullish(),
  createdFrom: ExperienceCreatedFrom.optional(),
});

export const ExperienceSchema = ExperienceBaseSchema.refine(
  ({ yearStart: startAt, yearEnd: endAt }) => {
    if (startAt && endAt) {
      const yearStart = startAt.getFullYear();
      const monthStart = startAt.getMonth();
      const yearEnd = endAt.getFullYear();
      const monthEnd = endAt.getMonth();

      return (
        yearStart < yearEnd || (yearStart === yearEnd && monthStart <= monthEnd)
      );
    }

    return true;
  },
  {
    message: t`La date de début ne peut pas être supérieur à la date de fin.`,
    path: ['yearStart'],
  }
)
  .refine(
    ({ yearStart: startAt, yearEnd: endAt }) => {
      if (startAt && endAt) {
        const yearStart = startAt.getFullYear();
        const monthStart = startAt.getMonth();
        const yearEnd = endAt.getFullYear();
        const monthEnd = endAt.getMonth();

        return (
          yearStart < yearEnd ||
          (yearStart === yearEnd && monthStart <= monthEnd)
        );
      }

      return true;
    },
    {
      message: t`La date de fin ne peut pas être inférieur à la date de début.`,
      path: ['yearEnd'],
    }
  )
  .refine(
    ({ domains = [], name }) => {
      if (!!name && typeof name === 'string') {
        if (!domains) {
          return false;
        }

        return domains.length > 0;
      }

      return true;
    },
    {
      message: t`Vous devez sélectionner au moins 1 domaine.`,
      path: ['domains'],
    }
  )
  .refine(
    ({ contractType }) => {
      return !!contractType;
    },
    {
      message: t`Le type de contract est obligatoire`,
      path: ['contractType'],
    }
  );

export const ExperienceSchemaNullable = ExperienceSchema.nullable();

export type ExperienceSchemaT = z.infer<typeof ExperienceSchema>;

type Nullable<T = {}> = {
  [K in keyof T]: T[K] | null;
};

export type ExperienceSchemaNullableT = Nullable<ExperienceSchemaT>;
